import { useMemo, useState } from "react";
import { CATEGORIES, MENU, buildWaLink, formatPrice } from "../data/menu";
import WaIcon from "./WaIcon";

export const MenuSection = () => {
    const [active, setActive] = useState("all");

    const items = useMemo(() => {
        if (active === "all") return MENU;
        return MENU.filter((i) => i.category === active);
    }, [active]);

    return (
        <section
            id="menu"
            className="relative bg-[#FDFBF7] py-16 md:py-24"
            data-testid="menu-section"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto">
                    <span className="inline-block text-[11px] tracking-[0.32em] font-bold text-[#D35400] bg-[#F5F0E6] rounded-full px-3 py-1.5">
                        OUR MENU
                    </span>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black mt-4 text-[#1F1A17] leading-tight flourish">
                        Crafted with fire, spice & patience
                    </h2>
                    <p className="text-[#7B716A] mt-5 leading-relaxed">
                        Every dish is prepared fresh to order. Tap the WhatsApp button
                        on any item — we'll confirm & get it to you hot.
                    </p>
                </div>

                {/* Category Tabs */}
                <div
                    className="mt-10 flex gap-2 overflow-x-auto justify-start md:justify-center no-scrollbar pb-2"
                    data-testid="category-tabs"
                >
                    {CATEGORIES.map((c) => {
                        const isActive = c.id === active;
                        return (
                            <button
                                key={c.id}
                                onClick={() => setActive(c.id)}
                                className={`btn-press whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                                    isActive
                                        ? "bg-[#D35400] text-white shadow-md shadow-[#D35400]/25"
                                        : "bg-[#F5F0E6] text-[#1F1A17] hover:bg-[#EADEC9]"
                                }`}
                                data-testid={`category-tab-${c.id}`}
                                aria-pressed={isActive}
                            >
                                {c.label}
                            </button>
                        );
                    })}
                </div>

                {/* Menu grid */}
                <div
                    className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
                    data-testid="menu-grid"
                >
                    {items.map((it) => (
                        <MenuCard key={it.id} item={it} />
                    ))}
                </div>

                {items.length === 0 && (
                    <p className="text-center text-[#7B716A] mt-10">
                        No items in this category yet.
                    </p>
                )}
            </div>
        </section>
    );
};

const MenuCard = ({ item }) => {
    const message = `Hi Kiraak Mandi! I'd like to order:\n• ${item.name} — ${formatPrice(
        item.price,
    )}\n\nPlease confirm availability.`;
    return (
        <article
            className="group bg-white rounded-3xl overflow-hidden border border-[#EADEC9] shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
            data-testid={`menu-card-${item.id}`}
        >
            <div className="relative w-full h-52 sm:h-56 overflow-hidden bg-[#F5F0E6]">
                <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                />
                {item.tag && (
                    <span className="absolute top-3 left-3 bg-[#1F1A17] text-white text-[10px] tracking-[0.24em] uppercase font-bold px-3 py-1.5 rounded-full">
                        {item.tag}
                    </span>
                )}
                <span className="absolute top-3 right-3 bg-white/95 backdrop-blur text-[#D35400] text-sm font-black px-3 py-1.5 rounded-full shadow">
                    {formatPrice(item.price)}
                </span>
            </div>
            <div className="p-6 flex flex-col gap-3 flex-1">
                <h3 className="font-display text-xl sm:text-2xl font-bold text-[#1F1A17] leading-tight">
                    {item.name}
                </h3>
                <p className="text-sm text-[#7B716A] leading-relaxed flex-1">
                    {item.desc}
                </p>
                <a
                    href={buildWaLink(message)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-press mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE53] text-white py-3 px-4 rounded-full font-bold"
                    data-testid={`order-btn-${item.id}`}
                >
                    <WaIcon className="w-5 h-5" />
                    Order on WhatsApp
                </a>
            </div>
        </article>
    );
};

export default MenuSection;
